/**
 * WP-V2-QC — QuestLayer: scena-overlay HUD misji (PLAN-V2 §7.2 / §10).
 * - aktywna misja = karta-przypinka przy krawędzi
 * - oferta = dymek Tak/Później (nad dającym lub na HUD)
 * - ukończenie = fanfara + zł (POST /api/quests/complete)
 *
 * NIE rejestruje się w registry.ts — integration owner doda 2 linie przy mergu.
 */
import Phaser from 'phaser';
import { getQuestById, GAME_HEIGHT, GAME_WIDTH, QUEST_REWARD_ZL } from '@pieskowo/shared';
import { BaseScene } from '../core/BaseScene.js';
import { MIN_TOUCH } from '../core/UiKit.js';
import { balanceBus } from '../core/balanceBus.js';
import { completeQuest } from './questClient.js';
import {
  acceptOffer,
  createQuestState,
  deferOffer,
  forceAccept,
  getActiveDef,
  markRewarded,
  offerFromGiver,
  offerQuest,
  questIdFromInteractPayload,
  recordStep,
  syncDay,
  type QuestStateSnapshot,
} from './questState.js';

export const INTERACT_BUS_EVENT = 'pieskowo:interact';

const PIN_W = 220;
const PIN_H = 88;
const HEART = '♥';

export type QuestLayerData = {
  /** Fake clock (testy / dev). */
  now?: () => number;
};

export class QuestLayer extends BaseScene {
  static readonly KEY = 'QuestLayer';

  private state: QuestStateSnapshot = createQuestState();
  private nowFn: () => number = () => Date.now();
  private pinRoot: Phaser.GameObjects.Container | null = null;
  private offerRoot: Phaser.GameObjects.Container | null = null;
  private fanfareRoot: Phaser.GameObjects.Container | null = null;
  private busHandler: ((payload: unknown) => void) | null = null;
  private rewarding = false;
  private offerScreenPos: { x: number; y: number } | null = null;
  private offerUiEnabled = true;

  constructor() {
    super(QuestLayer.KEY);
  }

  create(data: QuestLayerData = {}): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    if (data.now) this.nowFn = data.now;
    this.state = createQuestState({ now: this.nowFn });
    this.offerUiEnabled = true;

    this.busHandler = (payload: unknown) => this.onInteractBus(payload);
    this.game.events.on(INTERACT_BUS_EVENT, this.busHandler);

    // auto-oferta pierwszej dostępnej misji dnia (gdy brak aktywnej)
    this.state = offerQuest(this.state);
    this.redraw();

    this.events.on('shutdown', () => this.cleanup());
    this.events.on('destroy', () => this.cleanup());
  }

  /** Snapshot stanu (testy / E2E hook). */
  getState(): QuestStateSnapshot {
    return this.state;
  }

  /** Ustaw zegar (fake-clock). */
  setNow(now: () => number): void {
    this.nowFn = now;
    this.state = syncDay(this.state, { now: this.nowFn });
    this.redraw();
  }

  /** Pozycja ekranowa dającego (Misia/Lola) — dymek oferty. */
  setOfferScreenPos(x: number, y: number): void {
    this.offerScreenPos = { x, y };
    if (this.state.offeredQuestId) this.redrawOffer();
  }

  /** Oferta jest modalem Town; we wnętrzach stan questa zostaje, ale karta znika. */
  setOfferUiEnabled(enabled: boolean): void {
    if (this.offerUiEnabled === enabled) return;
    this.offerUiEnabled = enabled;
    this.redrawOffer();
  }

  /** Publiczne API: zaproponuj misję (opcjonalnie od dającego). */
  offer(questId?: string): void {
    this.state = syncDay(this.state, { now: this.nowFn });
    this.state = offerQuest(this.state, questId);
    this.redraw();
  }

  offerGiver(giver: 'misia' | 'lola' | 'kotek'): void {
    this.state = syncDay(this.state, { now: this.nowFn });
    this.state = offerFromGiver(this.state, giver);
    this.redraw();
  }

  /** E2E / dev: przyjmij misję od razu. */
  acceptQuestId(questId: string): void {
    this.state = forceAccept(this.state, questId);
    this.redraw();
  }

  acceptCurrentOffer(): void {
    this.state = acceptOffer(this.state);
    this.redraw();
  }

  deferCurrentOffer(): void {
    this.state = deferOffer(this.state);
    this.redraw();
  }

  private onInteractBus(payload: unknown): void {
    this.state = syncDay(this.state, { now: this.nowFn });
    const qid = questIdFromInteractPayload(payload);
    if (!qid) return;
    if (!this.state.active || this.state.active.questId !== qid) return;
    if (this.state.active.status !== 'active') return;

    const result = recordStep(this.state, qid);
    this.state = result.state;
    this.redrawPin();
    if (result.justCompleted) {
      void this.onQuestCompleted(qid);
    }
  }

  private async onQuestCompleted(questId: string): Promise<void> {
    if (this.rewarding) return;
    this.rewarding = true;
    this.showFanfare(questId, QUEST_REWARD_ZL);

    const res = await completeQuest({ questId });
    if (res.ok) {
      balanceBus.set(res.data.balance);
      this.state = markRewarded(this.state, questId);
      this.showFanfare(questId, res.data.rewardZl);
    } else {
      // offline / błąd — fanfara lokalna z katalogu; saldo bez zmian serwerowych
      this.state = markRewarded(this.state, questId);
    }
    this.rewarding = false;
    this.redraw();
  }

  private redraw(): void {
    this.redrawPin();
    this.redrawOffer();
  }

  private redrawPin(): void {
    this.pinRoot?.destroy(true);
    this.pinRoot = null;

    const active = this.state.active;
    if (!active || (active.status !== 'active' && active.status !== 'completed')) return;

    const def = getActiveDef(this.state);
    const title = def?.namePl ?? active.questId;
    const iconKey = def?.iconTexture ?? '';
    const x = GAME_WIDTH - PIN_W / 2 - 16;
    const y = 100 + PIN_H / 2;

    const bg = this.add
      .rectangle(0, 0, PIN_W, PIN_H, 0xfff8f0, 0.96)
      .setStrokeStyle(3, 0xffb6c1);
    bg.setName('quest-pin-bg');

    const icon = this.makeIcon(-PIN_W / 2 + 36, 0, iconKey, 0xff9ec8);

    const hearts = Array.from({ length: active.steps }, (_, i) =>
      i < active.currentStep ? HEART : '♡',
    ).join(' ');
    const label = this.add
      .text(12, -14, title, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
        fontStyle: 'bold',
        wordWrap: { width: PIN_W - 80 },
      })
      .setOrigin(0, 0.5);
    const stepTxt = this.add
      .text(12, 16, hearts, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#e74c6f',
      })
      .setOrigin(0, 0.5);
    stepTxt.setName('quest-pin-steps');
    stepTxt.setData('currentStep', active.currentStep);
    stepTxt.setData('steps', active.steps);
    stepTxt.setData('questId', active.questId);

    this.pinRoot = this.add.container(x, y, [bg, icon, label, stepTxt]);
    this.pinRoot.setDepth(4500);
    this.pinRoot.setScrollFactor(0);
    this.pinRoot.setName('quest-pin');
    this.pinRoot.setData('questId', active.questId);
    this.pinRoot.setSize(PIN_W, PIN_H);
  }

  private redrawOffer(): void {
    this.offerRoot?.destroy(true);
    this.offerRoot = null;

    if (!this.offerUiEnabled) return;
    const id = this.state.offeredQuestId;
    if (!id || this.state.active) return;
    const def = getQuestById(id);
    if (!def) return;

    const ox = this.offerScreenPos?.x ?? GAME_WIDTH / 2;
    const oy = this.offerScreenPos?.y ?? GAME_HEIGHT * 0.28;

    const cardW = 340;
    const cardH = 200;
    const dim = this.add
      .rectangle(0, 0, cardW, cardH, 0xfff8f0, 0.98)
      .setStrokeStyle(4, 0xffb6c1)
      .setInteractive();
    dim.on(
      'pointerdown',
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: { stopPropagation?: () => void },
      ) => {
        event.stopPropagation?.();
        pointer.event?.stopPropagation?.();
      },
    );

    const giverPl =
      def.giver === 'lola' ? 'Lola' : def.giver === 'misia' ? 'Misia' : 'Kotek';
    const title = this.add
      .text(0, -cardH / 2 + 28, `${giverPl} ma misję!`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const icon = this.makeIcon(-120, -10, def.iconTexture, 0xffd54f);

    const name = this.add
      .text(20, -20, def.namePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        wordWrap: { width: 180 },
      })
      .setOrigin(0, 0.5);

    const reward = this.add
      .text(20, 16, `+${def.rewardZl} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#c28b00',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    const btnH = Math.max(MIN_TOUCH, 64);
    const btnW = 130;
    const yesBg = this.add
      .rectangle(-80, cardH / 2 - 44, btnW, btnH, 0xb8e6d0)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const yesTxt = this.add
      .text(-80, cardH / 2 - 44, 'Tak', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#2d5a45',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    yesBg.setName('quest-offer-yes');
    yesBg.on(
      'pointerdown',
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: { stopPropagation?: () => void },
      ) => {
        event.stopPropagation?.();
        pointer.event?.stopPropagation?.();
        this.acceptCurrentOffer();
      },
    );

    const laterBg = this.add
      .rectangle(80, cardH / 2 - 44, btnW, btnH, 0xe8e0f0)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const laterTxt = this.add
      .text(80, cardH / 2 - 44, 'Później', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    laterBg.setName('quest-offer-later');
    laterBg.on(
      'pointerdown',
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: { stopPropagation?: () => void },
      ) => {
        event.stopPropagation?.();
        pointer.event?.stopPropagation?.();
        this.deferCurrentOffer();
      },
    );

    // ogonek dymka
    const tail = this.add.triangle(0, cardH / 2 + 8, 0, 0, 18, 22, -18, 22, 0xfff8f0);

    this.offerRoot = this.add.container(ox, oy, [
      dim,
      tail,
      title,
      icon,
      name,
      reward,
      yesBg,
      yesTxt,
      laterBg,
      laterTxt,
    ]);
    this.offerRoot.setDepth(4600);
    this.offerRoot.setScrollFactor(0);
    this.offerRoot.setName('quest-offer');
    this.offerRoot.setData('questId', id);
    this.offerRoot.setData('giver', def.giver);
  }

  private showFanfare(questId: string, rewardZl: number): void {
    this.fanfareRoot?.destroy(true);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const panel = this.add
      .rectangle(0, 0, 420, 200, 0xfff8e1, 0.98)
      .setStrokeStyle(5, 0xffd54f);
    const star = this.add
      .text(0, -48, '★', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '48px',
        color: '#ffc107',
      })
      .setOrigin(0.5);
    const title = this.add
      .text(0, 10, 'Brawo!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const gold = this.add
      .text(0, 56, `+${rewardZl} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#c28b00',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    gold.setName('quest-fanfare-gold');
    gold.setData('rewardZl', rewardZl);
    gold.setData('questId', questId);

    this.fanfareRoot = this.add.container(cx, cy, [panel, star, title, gold]);
    this.fanfareRoot.setDepth(5000);
    this.fanfareRoot.setScrollFactor(0);
    this.fanfareRoot.setName('quest-fanfare');
    this.fanfareRoot.setScale(0.6);
    this.fanfareRoot.setAlpha(0);

    this.tweens.add({
      targets: this.fanfareRoot,
      scale: 1,
      alpha: 1,
      duration: 280,
      ease: 'Back.Out',
    });

    // confetti-ish dots
    for (let i = 0; i < 12; i++) {
      const ang = (i / 12) * Math.PI * 2;
      const dot = this.add.circle(
        cx + Math.cos(ang) * 20,
        cy + Math.sin(ang) * 20,
        6,
        i % 2 === 0 ? 0xffb6c1 : 0xffd54f,
      );
      dot.setDepth(5001);
      dot.setScrollFactor(0);
      this.tweens.add({
        targets: dot,
        x: cx + Math.cos(ang) * 140,
        y: cy + Math.sin(ang) * 100,
        alpha: 0,
        duration: 900,
        ease: 'Cubic.Out',
        onComplete: () => dot.destroy(),
      });
    }

    this.time.delayedCall(2800, () => {
      this.fanfareRoot?.destroy(true);
      this.fanfareRoot = null;
    });
  }

  /** Placeholder ikony misji: tekstura lub kolorowy kształt. */
  private makeIcon(
    x: number,
    y: number,
    textureKey: string,
    color: number,
  ): Phaser.GameObjects.GameObject {
    if (textureKey && this.textures.exists(textureKey)) {
      return this.add.image(x, y, textureKey).setDisplaySize(56, 56);
    }
    // kolorowy kształt + emoji-fallback w tekście
    const g = this.add.container(x, y);
    const shape = this.add.circle(0, 0, 28, color).setStrokeStyle(3, 0xffffff);
    const glyph = this.add
      .text(0, 0, '!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    g.add([shape, glyph]);
    g.setSize(56, 56);
    return g;
  }

  private cleanup(): void {
    if (this.busHandler) {
      this.game.events.off(INTERACT_BUS_EVENT, this.busHandler);
      this.busHandler = null;
    }
    this.pinRoot?.destroy(true);
    this.offerRoot?.destroy(true);
    this.fanfareRoot?.destroy(true);
    this.pinRoot = null;
    this.offerRoot = null;
    this.fanfareRoot = null;
  }
}

/**
 * Uruchom overlay misji jeśli jeszcze nie aktywny.
 * Wywołanie z E2E / onboarding / integration owner po registry.
 */
export function ensureQuestOverlay(
  game: Phaser.Game,
  data?: QuestLayerData,
): void {
  const mgr = game.scene;
  if (mgr.isActive(QuestLayer.KEY) || mgr.isPaused(QuestLayer.KEY)) {
    return;
  }
  (mgr as unknown as { run: (key: string, data?: QuestLayerData) => void }).run(
    QuestLayer.KEY,
    data,
  );
}
